import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useBillingState } from '@/modules/billing/hooks/useBillingState';
import { popModal } from '@/redux/modals/actions';
import { isKeyEnter, isPromise } from '@/util/util';

type Props = {
  onDelete: () => void;
  name?: string;
  subtitle?: ReactNode;
  title?: string;
  primaryButtonText?: string;
  secondaryButtonText?: string;
  forceEnablePrimaryButton?: boolean;
  primaryDisabled?: boolean;
} & (
  | { confirm?: boolean; warning?: never; forceLabel?: never }
  | { warning?: ReactNode; forceLabel?: string; confirm?: never }
);

interface FormValues {
  confirmText: string;
  acknowledged: boolean;
}

/**
 * ModalDelete renders a destructive confirmation dialog that supports optional warning acknowledgements
 * and explicit text confirmation prior to deleting an entity.
 *
 * @param props - Component props controlling the delete behaviour and presentation.
 * @returns The delete confirmation dialog element.
 */
function ModalDelete({
  onDelete,
  name,
  subtitle,
  title,
  confirm,
  warning,
  forceLabel,
  primaryButtonText,
  secondaryButtonText,
  forceEnablePrimaryButton,
  primaryDisabled,
}: Props) {
  const { t } = useTranslation();
  const billingState = useBillingState();
  const dispatch = useDispatch();
  const form = useForm<FormValues>({
    defaultValues: { confirmText: '', acknowledged: false },
    mode: 'onChange',
  });
  const acknowledged = useWatch({ control: form.control, name: 'acknowledged' });
  const deleteName = useWatch({ control: form.control, name: 'confirmText' });
  const [isLoading, setIsLoading] = useState(false);
  const confirmInputRef = useRef<HTMLInputElement>(null);

  const valueToType = name || t('common.delete');

  // Focus the confirmation input when the modal opens
  useEffect(() => {
    if (confirm && confirmInputRef.current) {
      confirmInputRef.current.focus();
    }
  }, [confirm]);

  // Merge refs for react-hook-form and custom ref
  const mergeRefs = useCallback(
    (formRef: (instance: HTMLInputElement | null) => void) =>
      (element: HTMLInputElement | null) => {
        formRef(element);
        confirmInputRef.current = element;
      },
    []
  );

  const handleDelete = useCallback(async () => {
    setIsLoading(true);
    try {
      if (isPromise(onDelete)) {
        await onDelete();
      } else {
        onDelete();
      }
    } catch (error) {
      setIsLoading(false);
      throw error;
    }
  }, [onDelete]);

  const getPrimaryButtonText = useCallback(() => {
    if (primaryButtonText) {
      return primaryButtonText;
    }
    return t('common.delete');
  }, [primaryButtonText, t]);

  const handleCancel = useCallback(() => {
    dispatch(popModal());
  }, [dispatch]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        dispatch(popModal());
      }
    },
    [dispatch]
  );

  const normalizedValueToType = useMemo(() => valueToType?.trim().toUpperCase(), [valueToType]);

  const isBillingBlocked = billingState?.has_expired && !forceEnablePrimaryButton;

  const isConfirmationSatisfied = useMemo(() => {
    if (!confirm) {
      return true;
    }
    if (!normalizedValueToType) {
      return false;
    }
    return deleteName.trim().toUpperCase() === normalizedValueToType;
  }, [confirm, deleteName, normalizedValueToType]);

  // warning is informational only - no acknowledgement required
  // forceLabel requires explicit checkbox acknowledgement (currently unused)
  const isAcknowledgementRequired = Boolean(forceLabel);
  const isAcknowledged = !isAcknowledgementRequired || acknowledged;

  const enableButton =
    !isBillingBlocked &&
    isConfirmationSatisfied &&
    isAcknowledged &&
    !isLoading &&
    !primaryDisabled;

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (isKeyEnter(event) && enableButton) {
        event.preventDefault();
        handleDelete();
      }
    },
    [enableButton, handleDelete]
  );

  const primaryButtonDisabled = !enableButton;

  const tooltipMessage = forceEnablePrimaryButton
    ? ''
    : t('billing.account_expired', { 0: billingState?.plan_id }) + ' ' + t('billing.contact_admin');

  const deleteButton = (
    <Button
      type="button"
      onClick={handleDelete}
      variant="destructive"
      disabled={primaryButtonDisabled}
      isLoading={isLoading}
    >
      {getPrimaryButtonText()}
    </Button>
  );

  const deleteButtonWithTooltip =
    tooltipMessage && primaryButtonDisabled ? (
      <InfoTooltip content={tooltipMessage} size="auto">
        {deleteButton}
      </InfoTooltip>
    ) : (
      deleteButton
    );

  return (
    <Dialog open onOpenChange={handleOpenChange}>
      <DialogContent className="gap-6 sm:max-w-[450px]">
        <DialogHeader>
          <DialogTitle>{title || t('prompts.unsaved_changes.title')}</DialogTitle>
          <DialogDescription>{subtitle || t('prompts.unsaved_changes.subtitle')}</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <div className="flex flex-col gap-6">
            {warning}

            {!!forceLabel && (
              <FormField
                control={form.control}
                name="acknowledged"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center gap-3 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={(checked) => field.onChange(checked === true)}
                      />
                    </FormControl>
                    <FormLabel className="text-sm leading-none font-normal">{forceLabel}</FormLabel>
                  </FormItem>
                )}
              />
            )}

            {!!confirm && (
              <FormField
                control={form.control}
                name="confirmText"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('common.type_to_delete', { 0: valueToType })}</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder={valueToType}
                        ref={mergeRefs(field.ref)}
                        onKeyDown={handleKeyDown}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            )}
          </div>
        </Form>

        <DialogFooter>
          <Button type="button" variant="outline-neutral" onClick={handleCancel}>
            {secondaryButtonText || t('common.back_to_safety')}
          </Button>
          {deleteButtonWithTooltip}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ModalDelete;
